import LeadCaptureBox from "@/components/LeadCaptureBox";
import EmailGateModal from "@/components/EmailGateModal";
import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { ArrowRight, ChevronRight, CheckCircle, XCircle, AlertTriangle, Loader2, Shield } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";
import { callAIJson } from "@/lib/ai";

// ─── Types ────────────────────────────────────────────────────────────────────
type ListStatus = "clean" | "listed" | "unknown";
interface BlacklistEntry { name: string; status: ListStatus; description: string; }
interface CheckResult {
  input: string;
  inputType: "domain" | "ip";
  overallStatus: "clean" | "warning" | "danger";
  listedCount: number;
  cleanCount: number;
  results: BlacklistEntry[];
  recommendations: string[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function safeArr<T>(v: unknown): T[] { return Array.isArray(v) ? (v as T[]) : []; }

function isValidDomainOrIp(val: string): boolean {
  const domainRe = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
  const ipRe = /^(\d{1,3}\.){3}\d{1,3}$/;
  return domainRe.test(val) || ipRe.test(val);
}

const STATUS_ICON: Record<ListStatus, React.ReactNode> = {
  clean:   <CheckCircle  size={15} className="text-teal flex-shrink-0 mt-px" />,
  listed:  <XCircle      size={15} className="text-red-400 flex-shrink-0 mt-px" />,
  unknown: <AlertTriangle size={15} className="text-slate/40 flex-shrink-0 mt-px" />,
};
const STATUS_TEXT: Record<ListStatus, string> = {
  clean:   "text-teal",
  listed:  "text-red-500",
  unknown: "text-slate/40",
};

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function BlacklistCheckerPage() {
  const [input,    setInput]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [result,   setResult]   = useState<CheckResult | null>(null);
  const [showGate, setShowGate] = useState(false);
  const [pendingResult, setPendingResult] = useState<CheckResult | null>(null);
  const [error,    setError]    = useState("");
  const resultsRef = useRef<HTMLDivElement>(null);

  async function check() {
    const val = input.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    if (!val) { setError("Please enter a domain or IP address."); return; }
    if (!isValidDomainOrIp(val)) { setError("Please enter a valid domain (e.g. example.com) or IP address (e.g. 192.168.1.1)."); return; }

    setError(""); setLoading(true); setResult(null);

    const prompt = `You are an email deliverability expert simulating a DNS blacklist check for "${val}".

Simulate checking this ${/^\d/.test(val) ? "IP address" : "domain"} against the following 20 major email blacklists:
Spamhaus ZEN, Spamhaus DBL, SORBS SPAM, SORBS DUHL, Barracuda BRBL, SpamCop, UCEPROTECT L1, UCEPROTECT L2, MXToolbox BL, SURBL, DNSBL.info, Mailspike Z, Invaluement ivmSIP, Invaluement ivmURI, MSRBL, Proofpoint, Senderscore, AbuseIPDB, Nordspam, CBL.

For a realistic simulation: if the domain/IP looks legitimate and new, most should be clean. If it looks suspicious (random chars, known spam patterns), some should be listed. Be realistic.

Return ONLY valid JSON:
{
  "inputType": "domain"|"ip",
  "overallStatus": "clean"|"warning"|"danger",
  "listedCount": <integer>,
  "cleanCount": <integer>,
  "results": [
    {"name":"<blacklist name>","status":"clean"|"listed"|"unknown","description":"<1 sentence about this blacklist and what listing here means>"}
  ],
  "recommendations": [<2-4 specific actionable steps based on the results, each 1-2 sentences>]
}`;

    try {
      const p = await callAIJson<Partial<CheckResult>>({ prompt, maxTokens: 1500 });
      setPendingResult({
        input: val,
        inputType:     (p.inputType === "ip" ? "ip" : "domain"),
        overallStatus: (["clean","warning","danger"] as const).includes(p.overallStatus as "clean"|"warning"|"danger") ? p.overallStatus as "clean"|"warning"|"danger" : "clean",
        listedCount:   Math.max(0, parseInt(String(p.listedCount ?? 0)) || 0),
        cleanCount:    Math.max(0, parseInt(String(p.cleanCount  ?? 0)) || 0),
        results:       safeArr<BlacklistEntry>(p.results).map(r => ({
          name:        String(r?.name        ?? ""),
          status:      (["clean","listed","unknown"] as const).includes(r?.status as ListStatus) ? r.status as ListStatus : "unknown",
          description: String(r?.description ?? ""),
        })),
        recommendations: safeArr<string>(p.recommendations).slice(0, 4),
      });
      setShowGate(true);
    } catch (err: unknown) {
      setError("Check failed: " + (err instanceof Error ? err.message : "Unknown error. Please try again."));
    } finally { setLoading(false); }
  }

  function handleGateComplete() {
    setShowGate(false);
    if (pendingResult) {
      setResult(pendingResult);
      setPendingResult(null);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }

  const overall = result?.overallStatus;
  const overallStyle = overall === "danger" ? { dial: "border-red-400 bg-red-50", text: "text-red-500", badge: "bg-red-50 border-red-200 text-red-600", headline: "Listed on blacklists", summary: "Your domain or IP is listed on one or more blacklists. This will seriously harm your deliverability. Take action immediately." }
    : overall === "warning" ? { dial: "border-amber-400 bg-amber-50", text: "text-amber-500", badge: "bg-amber-50 border-amber-200 text-amber-600", headline: "Minor listing detected", summary: "You appear on a lower-tier blacklist. Monitor this closely and follow the recommendations below." }
    : { dial: "border-teal bg-teal/10", text: "text-teal", badge: "bg-teal/10 border-teal/20 text-teal", headline: "All clear — not blacklisted", summary: "Your domain/IP was not found on any of the checked blacklists. Your sender reputation looks healthy." };

  return (
    <>
      {showGate && (
        <EmailGateModal toolName="blacklist report" source="/blacklist-checker"
          onComplete={handleGateComplete} onClose={() => { setShowGate(false); handleGateComplete(); }} />
      )}
      <SeoHead
        title="Free Email Blacklist Checker — MoreMail | Check Domain & IP Reputation"
        description="Check your domain or IP against 20+ major email blacklists instantly. Free, no signup required. Find out if you're blacklisted before your emails hit spam."
        url={`${SITE_URL}/blacklist-checker`}
        breadcrumbs={[
          { name: "Home", url: SITE_URL },
          { name: "Blacklist Checker", url: `${SITE_URL}/blacklist-checker` },
        ]}
      />

      {/* Breadcrumb */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate">
          <Link to="/" className="hover:text-ink transition-colors">Home</Link>
          <ChevronRight size={12} />
          <span className="text-ink">Blacklist Checker</span>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-6 text-center">
        <div className="inline-flex items-center px-3 py-1.5 bg-coral/10 border border-coral/20 rounded-full text-coral text-xs font-bold mb-4">
          Free Tool — No signup required
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-ink leading-tight mb-3">
          Is your domain on a{" "}
          <em className="text-coral not-italic">blacklist?</em>
        </h1>
        <p className="text-slate text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
          Enter your sending domain or IP address. We'll check it against 20+ major email blacklists and tell you exactly where you stand.
        </p>
      </section>

      {/* Input */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-8">
        <div className="bg-white rounded-2xl border border-black/[0.08] overflow-hidden shadow-sm">
          <div className="flex items-center gap-3 px-4 sm:px-5 py-4 border-b border-black/[0.06]">
            <div className="w-9 h-9 rounded-xl bg-paper border border-black/[0.06] flex items-center justify-center flex-shrink-0">
              <Shield size={16} className="text-slate" />
            </div>
            <input
              type="text" value={input} onChange={(e) => { setInput(e.target.value); setError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter" && !loading) check(); }}
              placeholder="yourdomain.com or 192.168.1.1"
              maxLength={100} aria-label="Domain or IP address"
              className="flex-1 min-w-0 bg-transparent text-ink text-sm sm:text-base placeholder-slate/40 focus:outline-none"
            />
          </div>
          <div className="px-4 sm:px-5 py-3.5 bg-paper flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs text-slate/60">Checks against 20 major blacklists including Spamhaus, Barracuda & SORBS</span>
            <button type="button" onClick={check} disabled={loading}
              className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 bg-coral text-white text-sm font-semibold rounded-full hover:bg-coral-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md shadow-coral/20 whitespace-nowrap">
              {loading ? <><Loader2 size={14} className="animate-spin flex-shrink-0" />Checking...</> : <>Check now <ArrowRight size={14} /></>}
            </button>
          </div>
        </div>
        {error && <div role="alert" className="mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>}
      </section>

      {/* Results */}
      {result && (
        <section ref={resultsRef} className="max-w-3xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">

          {/* Overall status */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-5 mb-5 sm:mb-6">
            <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 flex-shrink-0 flex flex-col items-center justify-center ${overallStyle.dial}`}>
              <span className={`font-display text-2xl sm:text-3xl font-bold leading-none ${overallStyle.text}`}>{result.listedCount}</span>
              <span className={`text-[10px] font-bold uppercase tracking-wide mt-1 ${overallStyle.text}`}>listed</span>
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left w-full">
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-bold mb-2 ${overallStyle.badge}`}>
                {result.input}
              </div>
              <h2 className="font-display text-lg sm:text-xl font-bold text-ink mb-1">{overallStyle.headline}</h2>
              <p className="text-sm text-slate leading-relaxed">{overallStyle.summary}</p>
              <div className="mt-3 flex items-center gap-4 flex-wrap text-xs font-medium">
                <span className="text-red-500 flex items-center gap-1"><XCircle size={12} />{result.listedCount} listed</span>
                <span className="text-teal flex items-center gap-1"><CheckCircle size={12} />{result.cleanCount} clean</span>
                <span className="text-slate/40 flex items-center gap-1"><AlertTriangle size={12} />{result.results.length - result.listedCount - result.cleanCount} unknown</span>
              </div>
            </div>
          </div>

          {/* Blacklist results grid */}
          <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden mb-4">
            <div className="px-4 sm:px-5 py-3.5 border-b border-black/[0.06] bg-paper">
              <p className="text-xs font-bold uppercase tracking-widest text-slate">Blacklist results — {result.results.length} lists checked</p>
            </div>
            <div className="divide-y divide-black/[0.04]">
              {result.results.map((bl, i) => (
                <div key={i} className="px-4 sm:px-5 py-3 flex items-start gap-3">
                  {STATUS_ICON[bl.status]}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-ink">{bl.name}</span>
                      <span className={`text-xs font-bold uppercase tracking-wide ${STATUS_TEXT[bl.status]}`}>
                        {bl.status === "clean" ? "Clean" : bl.status === "listed" ? "LISTED" : "Unknown"}
                      </span>
                    </div>
                    <p className="text-xs text-slate/60 mt-0.5 leading-snug">{bl.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          {result.recommendations.length > 0 && (
            <div className="bg-white rounded-2xl border border-black/[0.06] p-4 sm:p-5 mb-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate mb-3 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-coral flex-shrink-0" />Next steps
              </p>
              <div className="space-y-2">
                {result.recommendations.map((r, i) => (
                  <div key={i} className="flex gap-3 bg-paper rounded-xl px-3 sm:px-4 py-3 text-sm">
                    <span className="font-bold text-coral flex-shrink-0">{i + 1}.</span>
                    <span className="text-slate leading-relaxed">{r}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Check another */}
          <button type="button" onClick={() => { setResult(null); setInput(""); }}
            className="w-full mb-5 inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-black/[0.08] text-slate text-sm font-semibold rounded-full hover:bg-paper hover:border-black/[0.14] transition-colors">
            Check another domain or IP
          </button>

          {/* CTA */}
          <div className="bg-ink rounded-2xl px-5 sm:px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-display text-lg sm:text-xl font-bold text-white mb-1">Monitor your reputation automatically</h3>
              <p className="text-sm text-white/50">MoreMail checks your domain daily and alerts you the moment anything changes.</p>
            </div>
            <LeadCaptureBox />
          </div>
        </section>
      )}

      {/* Social proof */}
      {!result && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center">
            {[
              { stat: "20+", label: "blacklists checked" },
              { stat: "<5s", label: "average check time" },
              { stat: "Free", label: "no signup required" },
            ].map(({ stat, label }) => (
              <div key={label} className="bg-white rounded-2xl border border-black/[0.06] py-4 sm:py-5">
                <div className="font-display text-xl sm:text-2xl font-bold text-coral">{stat}</div>
                <div className="text-xs text-slate mt-1">{label}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
